// src/components/TranscriptionHistory.jsx
import { useState } from 'react';
import LessonSummaryModal from './LessonSummaryModal';

function formatDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDuration(sec) {
  if (!sec) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

const SOURCE_ICONS = {
  upload:  '📁',
  record:  '🎤',
  youtube: '▶️',
  video:   '🎬',
  text:    '📝',
};

export default function TranscriptionHistory({ history, loading, onSelect, onDelete, onSaveSummary }) {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [summaryEntry, setSummaryEntry] = useState(null);

  const handleDelete = async (entry) => {
    setDeleting(entry.id);
    await onDelete(entry.id, entry.storagePath);
    setDeleting(null);
    setConfirmDelete(null);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
        <div style={{ color: '#475569', fontFamily: "'Courier New', monospace", fontSize: '13px' }}>
          Loading history...
        </div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div style={{
        textAlign: 'center', padding: '48px 24px',
        background: '#FFFFFF', borderRadius: '12px',
        border: '1px solid #E2E8F0', boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
      }}>
        <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎙️</div>
        <div style={{ color: '#475569', fontSize: '14px', marginBottom: '6px' }}>
          No recordings yet
        </div>
        <div style={{ color: '#64748B', fontSize: '12px', fontFamily: "'Courier New', monospace" }}>
          Your transcriptions will appear here after you record or upload audio
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {history.map((entry) => (
          <div
            key={entry.id}
            style={{
              background: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '10px',
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              transition: 'border-color 0.2s',
              boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#94A3B8'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#E2E8F0'}
          >
            {/* Thumbnail or icon */}
            {entry.thumbnail ? (
              <img
                src={entry.thumbnail}
                alt=""
                style={{
                  width: '72px', height: '40px', borderRadius: '4px',
                  objectFit: 'cover', flexShrink: 0,
                }}
              />
            ) : (
              <div style={{
                width: '72px', height: '40px', borderRadius: '4px',
                background: '#F1F5F9', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: '18px', flexShrink: 0,
              }}>
                {SOURCE_ICONS[entry.source] || '🎙️'}
              </div>
            )}

            {/* Info */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{
                fontWeight: '600', color: '#0F172A', fontSize: '13px',
                marginBottom: '3px', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {entry.title}
              </div>
              <div style={{
                display: 'flex', gap: '10px', alignItems: 'center',
                fontSize: '11px', color: '#64748B',
                fontFamily: "'Courier New', monospace", flexWrap: 'wrap',
              }}>
                <span>{formatDate(entry.createdAt)}</span>
                {entry.transcript?.audio_duration && (
                  <span>· {formatDuration(entry.transcript.audio_duration)}</span>
                )}
                {(entry.wordCount ?? entry.transcript?.words?.length ?? 0) > 0 && (
                  <span>· {(entry.wordCount ?? entry.transcript.words.length).toLocaleString()} words</span>
                )}
                <span style={{ color: '#CBD5E1' }}>·</span>
                <span>{SOURCE_ICONS[entry.source]} {entry.source}</span>
                {/* Summary badge */}
                {entry.summary && (
                  <span style={{
                    background: 'rgba(200,151,58,0.12)', border: '1px solid rgba(200,151,58,0.4)',
                    borderRadius: '4px', padding: '1px 6px',
                    color: '#B45309', fontSize: '10px', letterSpacing: '0.06em',
                  }}>
                    ✨ SUMMARY
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              <button
                onClick={() => onSelect(entry)}
                style={{
                  padding: '6px 14px', borderRadius: '6px',
                  background: 'transparent', border: '1px solid #CBD5E1',
                  color: '#475569', cursor: 'pointer', fontSize: '12px',
                  fontFamily: "'Courier New', monospace", letterSpacing: '0.06em',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.target.style.borderColor = '#2563EB'; e.target.style.color = '#2563EB'; }}
                onMouseLeave={e => { e.target.style.borderColor = '#CBD5E1'; e.target.style.color = '#475569'; }}
              >
                OPEN
              </button>

              {/* View summary button — only if summary exists */}
              {entry.summary && (
                <button
                  onClick={() => setSummaryEntry(entry)}
                  style={{
                    padding: '6px 10px', borderRadius: '6px',
                    background: 'transparent', border: '1px solid #CBD5E1',
                    color: '#B45309', cursor: 'pointer', fontSize: '14px',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.target.style.borderColor = '#B45309'; e.target.style.background = 'rgba(200,151,58,0.12)'; }}
                  onMouseLeave={e => { e.target.style.borderColor = '#CBD5E1'; e.target.style.background = 'transparent'; }}
                  title="View lesson summary"
                >
                  ✨
                </button>
              )}

              {/* Audio download */}
              {entry.audioUrl && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(entry.audioUrl, '_blank');
                  }}
                  style={{
                    padding: '6px 10px', borderRadius: '6px',
                    background: 'transparent', border: '1px solid #CBD5E1',
                    color: '#64748B', cursor: 'pointer', fontSize: '14px',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.target.style.borderColor = '#B45309'; e.target.style.color = '#B45309'; }}
                  onMouseLeave={e => { e.target.style.borderColor = '#CBD5E1'; e.target.style.color = '#64748B'; }}
                  title="Download audio"
                >
                  🎵
                </button>
              )}

              {confirmDelete === entry.id ? (
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={() => handleDelete(entry)}
                    disabled={deleting === entry.id}
                    style={{
                      padding: '6px 12px', borderRadius: '6px',
                      background: 'rgba(239,68,68,0.1)', border: '1px solid #EF4444',
                      color: '#B91C1C', cursor: 'pointer', fontSize: '12px',
                      fontFamily: "'Courier New', monospace",
                    }}
                  >
                    {deleting === entry.id ? '...' : 'CONFIRM'}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(null)}
                    style={{
                      padding: '6px 10px', borderRadius: '6px',
                      background: 'transparent', border: '1px solid #CBD5E1',
                      color: '#64748B', cursor: 'pointer', fontSize: '12px',
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(entry.id)}
                  style={{
                    padding: '6px 10px', borderRadius: '6px',
                    background: 'transparent', border: '1px solid #CBD5E1',
                    color: '#64748B', cursor: 'pointer', fontSize: '14px',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.target.style.borderColor = '#EF4444'; e.target.style.color = '#EF4444'; }}
                  onMouseLeave={e => { e.target.style.borderColor = '#CBD5E1'; e.target.style.color = '#64748B'; }}
                >
                  🗑
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Summary modal — opened from history */}
      {summaryEntry && (
        <LessonSummaryModal
          transcript={summaryEntry.transcript?.text || ""}
          language={summaryEntry.language || "en"}
          existingSummary={summaryEntry.summary}
          onClose={() => setSummaryEntry(null)}
          onSave={(updatedSummary) =>
            onSaveSummary ? onSaveSummary(summaryEntry.id, updatedSummary) : false
          }
        />
      )}
    </>
  );
}